import React, { useState } from 'react';
import { Flame, Droplet, RotateCcw } from 'lucide-react';
import { K, HOSE as DEFAULT_HOSE, APPLIANCES as DEFAULT_APPLIANCES, SB_TIPS as DEFAULT_TIPS, inputStyle } from '../constants';
import { calcFL, calcGPM, uid } from '../utils/formulas';
import { usePersist } from '../hooks/usePersist';
import { NI, Sel, Lbl, SecHd, Card, Metric, Btn, AddBtn, MathBox } from '../components/ui';

function createDischarge(num) {
  return {
    id: uid(),
    name: `Discharge ${num}`,
    mode: 'attack',
    nzT: 'elkhartLP',
    tip: '7/8"',
    fgGPM: 150,
    np: 75,
    targetInlet: 20,
    wild: false,
    segs: [{ id: uid(), type: 'hose', diam: '1¾"', len: 200, gpm: 150, elv: 0 }],
  };
}

export function HoseLayout({ showMath, calcDefaults }) {
  const HOSE = calcDefaults?.hose || DEFAULT_HOSE;
  const APPLIANCES = calcDefaults?.appliances || DEFAULT_APPLIANCES;
  const SB_TIPS = calcDefaults?.tips || DEFAULT_TIPS;
  const elevFactor = calcDefaults?.ops?.elevFactor ?? 0.5;
  const getHoseOpts = (wild) => Object.fromEntries(Object.entries(HOSE).filter(([, v]) => wild || !v.wildland));
  const getTipOpts = (wild) => Object.fromEntries(Object.entries(SB_TIPS).filter(([, v]) => wild || !v.wildland));
  const [discharges, setDischarges] = useState([createDischarge(1)]);
  const [pumpPSI, setPumpPSI] = useState(150);
  const [expanded, setExpanded] = useState({});
  const [saves, setSaves] = usePersist('hydro_layout_saves', []);
  const [saveName, setSaveName] = useState('');

  const updateDischarge = (id, field, value) => setDischarges((ds) => ds.map((d) => (d.id === id ? { ...d, [field]: value } : d)));
  const addDischarge = () => setDischarges((ds) => [...ds, createDischarge(ds.length + 1)]);
  const removeDischarge = (id) => setDischarges((ds) => ds.filter((d) => d.id !== id));
  const toggleExpanded = (id) => setExpanded((e) => ({ ...e, [id]: e[id] === false ? true : false }));

  const addSegment = (dId, type) => setDischarges((ds) => ds.map((d) => {
    if (d.id !== dId) return d;
    const seg = type === 'hose'
      ? { id: uid(), type: 'hose', diam: '1¾"', len: 100, gpm: 150, elv: 0 }
      : { id: uid(), type: 'appliance', app: 'Gated Wye', elv: 0 };
    return { ...d, segs: [...d.segs, seg] };
  }));

  const updateSegment = (dId, sId, field, value) => setDischarges((ds) => ds.map((d) => {
    if (d.id !== dId) return d;
    return { ...d, segs: d.segs.map((s) => (s.id === sId ? { ...s, [field]: value } : s)) };
  }));

  const removeSegment = (dId, sId) => setDischarges((ds) => ds.map((d) => {
    if (d.id !== dId) return d;
    return { ...d, segs: d.segs.filter((s) => s.id !== sId) };
  }));

  const calcDischarge = (d) => {
    // Determine flow for this discharge to evaluate appliance thresholds
    const dischargeGPM = d.mode === 'supply'
      ? (d.segs.find((s) => s.type === 'hose')?.gpm ?? 0)
      : (d.nzT === 'sb' ? Math.round(calcGPM(SB_TIPS[d.tip]?.d ?? 0, d.np)) : d.fgGPM);

    const calcs = d.segs.map((s) => {
      if (s.type === 'hose') {
        const fl = calcFL(HOSE[s.diam].c, s.gpm, s.len);
        const elev = elevFactor * s.elv;
        return { s, fl, elev, total: fl + elev };
      }
      const app = APPLIANCES[s.app];
      const loss = app && (!app.minGPM || dischargeGPM >= app.minGPM) ? app.loss : 0;
      return { s, fl: loss, elev: 0, total: loss, skipped: app?.minGPM && dischargeGPM < app.minGPM };
    });

    const totalLoss = calcs.reduce((a, c) => a + c.total, 0);
    if (d.mode === 'supply') {
      const reqPDP = totalLoss + (d.targetInlet ?? 20);
      return { nozzleGPM: dischargeGPM, totalLoss, reqPDP, calcs };
    }

    const reqPDP = totalLoss + d.np;
    return { nozzleGPM: dischargeGPM, totalLoss, reqPDP, calcs };
  };

  const results = discharges.map((d) => ({ d, ...calcDischarge(d) }));
  const maxPDP = results.length ? Math.max(...results.map((r) => r.reqPDP)) : 0;
  const totalGPM = results.reduce((a, r) => a + r.nozzleGPM, 0);
  const residual = pumpPSI - maxPDP;
  const allOk = residual >= 0;

  return (
    <div>
      <SecHd title="Layout Builder" sub="Build each discharge separately. The pump summary tells you what the engine actually needs to deliver." />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
        <div><Lbl t="Engine Pump PSI" /><NI v={pumpPSI} set={setPumpPSI} min={0} max={400} /></div>
        <Metric label="Total Flow" value={`${totalGPM} GPM`} sub={`${discharges.length} line${discharges.length !== 1 ? 's' : ''}`} col={K.info} sm />
      </div>

      {results.map(({ d, nozzleGPM, totalLoss, reqPDP, calcs }) => {
        const open = expanded[d.id] !== false;
        const status = reqPDP > pumpPSI ? K.bad : reqPDP > pumpPSI * 0.9 ? K.warn : K.ok;
        return (
          <div key={d.id} style={{ background: K.bg1, border: `1px solid ${K.bdr}`, borderLeft: `5px solid ${d.mode === 'supply' ? K.info : K.red}`, borderRadius: 10, marginBottom: 10, overflow: 'hidden' }}>
            <div style={{ padding: '10px 12px', display: 'flex', gap: 8, alignItems: 'center' }}>
              <button onClick={() => toggleExpanded(d.id)} style={{ background: 'none', border: 'none', color: K.t2, cursor: 'pointer', fontSize: 13 }}>{open ? '▲' : '▼'}</button>
              <input value={d.name} onChange={(e) => updateDischarge(d.id, 'name', e.target.value)} style={{ ...inputStyle(), background: 'transparent', border: 'none', padding: 0, flex: 1, fontWeight: 700, minWidth: 0 }} />
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ fontFamily: K.fMono, fontSize: 18, fontWeight: 500, color: d.mode === 'supply' ? K.info : K.ok }}>{nozzleGPM}<span style={{ fontSize: 11, color: K.t2, fontWeight: 400 }}> gpm</span></span>
                <span style={{ fontFamily: K.fMono, fontSize: 18, fontWeight: 500, color: status }}>{reqPDP.toFixed(0)}<span style={{ fontSize: 11, color: K.t2, fontWeight: 400 }}> psi</span></span>
                {discharges.length > 1 ? <button onClick={() => removeDischarge(d.id)} style={{ background: 'none', border: 'none', color: K.t2, cursor: 'pointer', fontSize: 18 }}>✕</button> : null}
              </div>
            </div>

            {open ? (
              <div style={{ padding: '0 12px 14px' }}>
                <div style={{ paddingTop: 8, borderTop: `1px solid ${K.bdr}`, marginBottom: 10 }}>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                    <button onClick={() => updateDischarge(d.id, 'mode', 'attack')} style={{ flex: 1, background: d.mode === 'attack' ? K.red : K.bg3, border: `1px solid ${d.mode === 'attack' ? K.red2 : K.bdr2}`, color: d.mode === 'attack' ? K.t0 : K.t1, padding: '10px', borderRadius: 8, cursor: 'pointer', fontFamily: K.fSans, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><Flame size={16} color={d.mode === 'attack' ? K.t0 : K.red} strokeWidth={1.75} /> Attack</button>
                    <button onClick={() => updateDischarge(d.id, 'mode', 'supply')} style={{ flex: 1, background: d.mode === 'supply' ? K.info : K.bg3, border: `1px solid ${d.mode === 'supply' ? K.info : K.bdr2}`, color: d.mode === 'supply' ? K.t0 : K.t1, padding: '10px', borderRadius: 8, cursor: 'pointer', fontFamily: K.fSans, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><Droplet size={16} color={d.mode === 'supply' ? K.t0 : K.info} strokeWidth={1.75} /> Supply</button>
                    <button onClick={() => updateDischarge(d.id, 'wild', !d.wild)} style={{ background: d.wild ? `${K.ok}22` : K.bg3, border: `1px solid ${d.wild ? K.ok : K.bdr2}`, color: d.wild ? K.ok : K.t2, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: K.fSans, fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>{d.wild ? '●' : '○'} Wildland</button>
                  </div>

                  {d.mode === 'attack' ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div><Lbl t="Nozzle Type" /><Sel v={d.nzT} set={(v) => { updateDischarge(d.id, 'nzT', v); if (v === 'sb') updateDischarge(d.id, 'np', 50); else if (v === 'elkhartLP') updateDischarge(d.id, 'np', 75); else updateDischarge(d.id, 'np', 100); }}><option value="sb">Elkhart Smoothbore</option><option value="auto">Automatic</option><option value="elkhartLP">Elkhart LP 75</option></Sel></div>
                      {d.nzT === 'sb' ? (
                        <div><Lbl t="Tip Size" /><Sel v={d.tip} set={(v) => updateDischarge(d.id, 'tip', v)}>{Object.keys(getTipOpts(d.wild)).map((k) => <option key={k}>{k}</option>)}</Sel></div>
                      ) : (
                        <div><Lbl t="Flow (GPM)" /><NI v={d.fgGPM} set={(v) => updateDischarge(d.id, 'fgGPM', v)} min={0} max={500} /></div>
                      )}
                      <div><Lbl t="Nozzle Pressure (psi)" /><NI v={d.np} set={(v) => updateDischarge(d.id, 'np', v)} min={0} max={200} /></div>
                      <div style={{ background: K.bg2, borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <div style={{ fontFamily: K.fSans, fontSize: 11, color: K.t2, letterSpacing: 0.4, marginBottom: 4 }}>Nozzle flow</div>
                        <div style={{ fontFamily: K.fMono, fontSize: 24, fontWeight: 500, color: K.ok }}>{nozzleGPM} <span style={{ fontSize: 12, color: K.t2, fontWeight: 400 }}>GPM</span></div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div><Lbl t="Target Inlet PSI" /><NI v={d.targetInlet ?? 20} set={(v) => updateDischarge(d.id, 'targetInlet', v)} min={0} max={150} /></div>
                      <div style={{ background: K.bg2, borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <div style={{ fontFamily: K.fSans, fontSize: 11, color: K.t2, letterSpacing: 0.4, marginBottom: 4 }}>Supply flow</div>
                        <div style={{ fontFamily: K.fMono, fontSize: 24, fontWeight: 500, color: K.info }}>{nozzleGPM} <span style={{ fontSize: 12, color: K.t2, fontWeight: 400 }}>GPM</span></div>
                      </div>
                    </div>
                  )}
                </div>

                {calcs.map((c, i) => {
                  const isH = c.s.type === 'hose';
                  const lcol = c.total > 60 ? K.bad : c.total > 30 ? K.warn : K.ok;
                  return (
                    <div key={c.s.id} style={{ background: K.bg2, border: `1px solid ${K.bdr}`, borderLeft: `3px solid ${isH ? HOSE[c.s.diam].col : K.gold}`, borderRadius: 8, padding: 10, marginBottom: 6 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontFamily: K.fMono, fontSize: 12, color: K.t3, fontWeight: 500 }}>{String(i + 1).padStart(2, '0')}</span>
                        <span style={{ fontFamily: K.fMono, fontSize: 24, fontWeight: 500, color: lcol }}>{c.total.toFixed(1)} <span style={{ fontSize: 12, color: K.t2, fontWeight: 400 }}>psi</span></span>
                        <button onClick={() => removeSegment(d.id, c.s.id)} style={{ background: 'none', border: 'none', color: K.t2, cursor: 'pointer', fontSize: 18 }}>✕</button>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: isH ? 6 : 0 }}>
                        {isH ? (
                          <div><Lbl t="Hose Size" /><Sel v={c.s.diam} set={(v) => updateSegment(d.id, c.s.id, 'diam', v)}>{Object.keys(getHoseOpts(d.wild)).map((k) => <option key={k}>{k}</option>)}</Sel></div>
                        ) : (
                          <div><Lbl t="Appliance" /><Sel v={c.s.app} set={(v) => updateSegment(d.id, c.s.id, 'app', v)}>{Object.keys(APPLIANCES).map((a) => <option key={a}>{a}</option>)}</Sel></div>
                        )}
                        <div><Lbl t="Elevation (ft)" /><NI v={c.s.elv} set={(v) => updateSegment(d.id, c.s.id, 'elv', v)} min={-500} max={500} /></div>
                      </div>
                      {isH ? (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                          <div><Lbl t="Length (ft)" /><NI v={c.s.len} set={(v) => updateSegment(d.id, c.s.id, 'len', v)} min={0} max={5000} /></div>
                          <div><Lbl t="GPM" /><NI v={c.s.gpm} set={(v) => updateSegment(d.id, c.s.id, 'gpm', v)} min={0} max={2000} /></div>
                        </div>
                      ) : null}
                      {c.skipped && (
                        <div style={{ fontSize: 10, color: K.t3, marginTop: 4, fontStyle: 'italic' }}>No loss — flow below {APPLIANCES[c.s.app]?.minGPM} GPM</div>
                      )}
                    </div>
                  );
                })}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 8, marginBottom: 10 }}>
                  <AddBtn col={K.red} onClick={() => addSegment(d.id, 'hose')}>+ Hose</AddBtn>
                  <AddBtn col={K.red} onClick={() => addSegment(d.id, 'appliance')}>+ Appliance</AddBtn>
                </div>

                <div style={{ background: K.bg, borderRadius: 8, padding: '10px 12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <div style={{ fontFamily: K.fSans, fontSize: 11, color: K.t3, letterSpacing: 0.3, marginBottom: 3 }}>Total loss</div>
                    <div style={{ fontFamily: K.fMono, fontSize: 22, fontWeight: 500, color: K.t1 }}>{totalLoss.toFixed(1)} psi</div>
                  </div>
                  <div>
                    <div style={{ fontFamily: K.fSans, fontSize: 11, color: K.t3, letterSpacing: 0.3, marginBottom: 3 }}>Required PDP</div>
                    <div style={{ fontFamily: K.fMono, fontSize: 22, fontWeight: 500, color: status }}>{reqPDP.toFixed(1)} psi</div>
                  </div>
                </div>

                <MathBox show={showMath} steps={[
                  { l: 'Formula', f: d.mode === 'supply' ? 'PDP = target inlet + ΣFL + ΣElev + app' : 'PDP = NP + ΣFL + ΣElev + app' },
                  ...calcs.map((c, idx) => c.s.type === 'hose'
                    ? { l: `Seg ${idx + 1}`, f: `${HOSE[c.s.diam].c}×(${c.s.gpm}/100)²×(${c.s.len}/100)`, r: `${c.fl.toFixed(1)} psi` }
                    : { l: c.s.app, f: c.skipped ? `<${APPLIANCES[c.s.app]?.minGPM} GPM — no loss` : 'fixed', r: `${c.fl} psi` }),
                  { l: 'Required PDP', f: d.mode === 'supply' ? `${d.targetInlet} + ${totalLoss.toFixed(1)}` : `${d.np} + ${totalLoss.toFixed(1)}`, r: `${reqPDP.toFixed(1)} psi` },
                ]} />
              </div>
            ) : null}
          </div>
        );
      })}

      <div style={{ marginBottom: 16 }}><AddBtn col={K.red} onClick={addDischarge}>+ Add Discharge</AddBtn></div>

      <div style={{ background: allOk ? `${K.ok}18` : `${K.bad}18`, border: `1px solid ${allOk ? K.ok : K.bad}`, borderRadius: 10, padding: 14, marginBottom: 20 }}>
        <div style={{ fontFamily: K.fSans, fontSize: 11, fontWeight: 600, color: K.t2, letterSpacing: 0.4, marginBottom: 10 }}>Pump summary</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
          {results.map(({ d, nozzleGPM, reqPDP }) => (
            <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontFamily: K.fSans, fontSize: 11, color: K.t1 }}>{d.name}</span>
              <span style={{ fontFamily: K.fMono, fontSize: 11, color: K.t2 }}>{nozzleGPM} GPM</span>
              <span style={{ fontFamily: K.fMono, fontSize: 18, fontWeight: 500, color: reqPDP > pumpPSI ? K.bad : K.ok }}>{reqPDP.toFixed(0)} psi</span>
            </div>
          ))}
        </div>
        <div className="hc-grid-3" style={{ borderTop: `1px solid ${allOk ? K.ok : K.bad}55`, paddingTop: 10, gap: 8 }}>
          <Metric label="Total GPM" value={`${totalGPM}`} col={K.info} sm />
          <Metric label="Pump Needs" value={`${maxPDP.toFixed(0)} psi`} col={allOk ? K.gold : K.bad} sm />
          <Metric label={allOk ? 'Residual' : 'Deficit'} value={`${residual > 0 ? '+' : ''}${residual.toFixed(0)}`} col={allOk ? K.ok : K.bad} sm />
        </div>
      </div>

      <Card accent={K.teal}>
        <div style={{ fontFamily: K.fSans, fontSize: 11, fontWeight: 600, color: K.teal, letterSpacing: 0.4, marginBottom: 10 }}>Save layout</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, marginBottom: 10 }}>
          <input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Warehouse fire / 2nd floor attack" style={inputStyle()} />
          <Btn onClick={() => {
            if (!saveName.trim()) return;
            setSaves((s) => [...s, { name: saveName.trim(), discharges, pumpPSI }]);
            setSaveName('');
          }} col={K.red}>Save</Btn>
        </div>
        {saves.length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {saves.map((sv, i) => (
              <button key={`${sv.name}-${i}`} onClick={() => {
                setDischarges(sv.discharges);
                setPumpPSI(sv.pumpPSI);
              }} style={{ background: K.bg2, border: `1px solid ${K.bdr}`, color: K.t0, padding: '10px 12px', borderRadius: 8, textAlign: 'left', cursor: 'pointer' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: K.fSans, color: K.teal, fontWeight: 600 }}><RotateCcw size={14} color={K.teal} strokeWidth={1.75} /> {sv.name}</span>
              </button>
            ))}
          </div>
        ) : null}
      </Card>
    </div>
  );
}
